import { Component, OnInit } from '@angular/core';
import { UntypedFormArray, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';
import { MatDialog } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { Select } from '@ngxs/store';
import { TranslateService } from '@ngx-translate/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Observable, Subject, firstValueFrom } from 'rxjs';
import { debounceTime, filter } from 'rxjs/operators';
import { Organization } from 'app/models/organization.model';
import { OrganizationsState } from 'app/state-management/states/organizations.states';
import { DishesService } from 'app/services/dishes.service';
import { SnackbarService } from 'app/services/snackbar.service';
import { ConfirmDialogComponent, ConfirmDialogData } from 'app/shared/components/confirm-dialog/confirm-dialog.component';
import * as MenuActions from 'app/reducers/menu.actions';
import * as MenuSelectors from 'app/reducers/menu.selectors';
import { selectHasPermission } from 'app/reducers/context.selectors';
import { IUnsavedSettingsAware } from 'app/pages/admin/app-settings/settings-scope/unsaved-settings.guard';
import {
  EMPTY_INSTRUCTION,
  ISpecialConvertInstruction,
} from './models/special-convert-instruction.model';
import {
  IModifierRow,
  ISiOption,
  ISizePairRow,
  ISpecialInstructionsForm,
  SiMode,
} from './models/special-instructions-form.model';
import {
  canonicalStringify,
  normalizeInstruction,
  parseInstructionJson,
  stringifyInstruction,
  toInstruction,
  toRows,
} from './special-instructions.converter';
import {
  SpecialInstructionsApiService,
  describeSpecialInstructionsError,
  extractServerMessage,
} from './special-instructions-api.service';

/** Пауза перед разбором введённого JSON: на каждое изменение пересобирается вся форма. */
const JSON_PARSE_DEBOUNCE_MS = 300;

// Permission code from HubPermissionCode enum.
const SPECIAL_INSTRUCTION_EDIT = 1000082;

/**
 * Редактирование iiko special convert instructions.
 *
 * Единственный источник правды — реактивная форма. JSON-текст производен и живёт, только пока
 * открыт соответствующий режим: валидный JSON сразу применяется к форме, поэтому «есть ли
 * изменения» и доступность кнопки сохранения одинаково достоверны в обоих режимах.
 */
@UntilDestroy()
@Component({
  selector: 'app-special-instructions',
  templateUrl: './special-instructions.component.html',
  styleUrls: ['./special-instructions.component.scss'],
})
export class SpecialInstructionsComponent implements OnInit, IUnsavedSettingsAware {
  @Select(OrganizationsState.getSelectedOrganization) selectedOrganization$: Observable<Organization>;

  public mode: SiMode = 'table';
  public form: UntypedFormGroup;
  public expanded: boolean[] = [];

  /** Право на редактирование спец-инструкций (сисадмин получает true). */
  public canEdit$: Observable<boolean> = this.store.select(selectHasPermission(SPECIAL_INSTRUCTION_EDIT));

  public jsonText = '';
  public jsonError: string | null = null;

  public isLoading = false;
  public isMenuLoading = false;
  public isSaving = false;
  public loadError: string | null = null;
  public isForbidden = false;
  public forbiddenDetails: string | null = null;
  /** Меню не удалось получить — экран работает, но в полях видны только GUID-ы. */
  public menuUnavailable = false;

  /** Организация, выбранная в шапке: по ней грузится меню. */
  public organizationId = '';
  /** Корневая организация: именно её id уходит в API — инструкции общие на всё дерево. */
  public targetOrganizationId = '';
  public targetOrganizationName = '';
  public isBranch = false;

  public modifierOptions: ISiOption[] = [];
  public sizeOptions: ISiOption[] = [];

  private modifierById = new Map<string, ISiOption>();
  private sizeById = new Map<string, ISiOption>();
  /** Снимок того, что лежит на бэке, — база для hasChanges. */
  private saved: ISpecialConvertInstruction = EMPTY_INSTRUCTION;
  private jsonInput$ = new Subject<string>();

  constructor(
    private fb: UntypedFormBuilder,
    private store: Store,
    private api: SpecialInstructionsApiService,
    private dishesService: DishesService,
    private dialog: MatDialog,
    private snackbar: SnackbarService,
    private translate: TranslateService,
  ) {
  }

  public get modifiers(): UntypedFormArray {
    return this.form.get('modifiers') as UntypedFormArray;
  }

  public get ignoreModifiersControl(): UntypedFormControl {
    return this.form.get('ignoreModifiers') as UntypedFormControl;
  }

  /**
   * Контракт IUnsavedSettingsAware. Сравниваем канонический JSON, а не form.dirty: пользователь
   * может добавить строку и тут же удалить её — спрашивать подтверждение не за что.
   */
  public get hasChanges(): boolean {
    if (!this.form) {
      return false;
    }

    if (this.jsonError) {
      return true;
    }

    return canonicalStringify(toInstruction(this.readForm())) !== canonicalStringify(this.saved);
  }

  public get canSave(): boolean {
    return !this.isSaving && !this.jsonError && this.form?.valid && this.hasChanges;
  }

  public async ngOnInit(): Promise<void> {
    this.subscribeToJsonInput();
    this.subscribeToDictionaries();
    this.buildForm({modifiers: [], ignoreModifiers: []});

    const organization = await firstValueFrom(this.selectedOrganization$);

    if (!organization) {
      this.loadError = this.translate.instant('MENU.SPECIAL_INSTRUCTIONS.ERRORS.NO_ORGANIZATION');
      return;
    }

    this.organizationId = organization.id;
    this.targetOrganizationId = organization.parentId ?? organization.id;
    this.targetOrganizationName = organization.name;
    this.isBranch = !!organization.parentId;

    // Справочник и сами инструкции независимы: экран не ждёт меню, чтобы начать редактирование.
    void this.ensureMenuLoaded();
    this.loadInstruction();
  }

  public sizePairs(modifierIndex: number): UntypedFormArray {
    return this.modifiers.at(modifierIndex).get('sizePairs') as UntypedFormArray;
  }

  public addModifier(): void {
    this.modifiers.push(this.createModifierGroup({modifierId: '', isRemove: false, sizePairs: []}));
    this.expanded[this.modifiers.length - 1] = true;
  }

  public removeModifier(index: number): void {
    this.modifiers.removeAt(index);
    this.expanded.splice(index, 1);
  }

  public addSizePair(modifierIndex: number): void {
    this.sizePairs(modifierIndex).push(this.createSizePairGroup({sourceSizeId: '', targetSizeId: ''}));
  }

  public removeSizePair(modifierIndex: number, pairIndex: number): void {
    this.sizePairs(modifierIndex).removeAt(pairIndex);
  }

  public onIgnoreModifiersChange(ids: string[]): void {
    this.ignoreModifiersControl.setValue(ids);
    this.ignoreModifiersControl.markAsDirty();
  }

  public setMode(mode: SiMode): void {
    if (mode === this.mode) {
      return;
    }

    if (mode === 'json') {
      // Сериализуем строго при входе в режим, чтобы не переписывать текст под курсором.
      this.jsonText = stringifyInstruction(toInstruction(this.readForm()));
      this.jsonError = null;
      this.mode = 'json';
      return;
    }

    // С невалидным JSON уходить нельзя: правки в текстовом поле молча потерялись бы.
    if (this.jsonError) {
      this.snackbar.error(this.translate.instant('MENU.SPECIAL_INSTRUCTIONS.JSON.FIX_BEFORE_SWITCH'));
      return;
    }

    this.mode = 'table';
  }

  public onJsonInput(text: string): void {
    this.jsonText = text;
    this.jsonInput$.next(text);
  }

  /** Перегоняет текст через форму, попутно нормализуя мусор. */
  public formatJson(): void {
    if (this.jsonError) {
      return;
    }

    this.jsonText = stringifyInstruction(toInstruction(this.readForm()));
  }

  public isUnknownModifier(id: string): boolean {
    return !!id && !this.modifierById.has(id);
  }

  public displayModifier(id: string): string {
    const option = this.modifierById.get(id);

    return option ? `${option.label} (${id})` : id;
  }

  /** Один модификатор в двух блоках: при сохранении останется только последний. */
  public isDuplicateModifier(index: number): boolean {
    const ids = this.readForm().modifiers.map(row => (row.modifierId ?? '').trim());
    const current = ids[index];

    return !!current && ids.filter(id => id === current).length > 1;
  }

  /** Один исходный размер дважды внутри модификатора: в JSON ключ будет один. */
  public isDuplicateSizePair(modifierIndex: number, pairIndex: number): boolean {
    const pairs: ISizePairRow[] = this.readForm().modifiers[modifierIndex]?.sizePairs ?? [];
    const current = (pairs[pairIndex]?.sourceSizeId ?? '').trim();

    return !!current && pairs.filter(pair => (pair.sourceSizeId ?? '').trim() === current).length > 1;
  }

  public save(): void {
    if (!this.canSave) {
      return;
    }

    const instruction = toInstruction(this.readForm());
    const data: ConfirmDialogData = {
      title: this.translate.instant('MENU.SPECIAL_INSTRUCTIONS.SAVE_CONFIRM_TITLE'),
      message: this.translate.instant('MENU.SPECIAL_INSTRUCTIONS.SAVE_CONFIRM', {
        organization: this.targetOrganizationName || this.targetOrganizationId,
        modifiers: Object.keys(instruction.modifierToSizes).length,
        ignored: instruction.ignoreModifiers.length,
      }),
    };

    // Подтверждение обязательно: бэк перезаписывает инструкции целиком, мержа нет.
    this.dialog.open(ConfirmDialogComponent, {data})
      .afterClosed()
      .pipe(filter(Boolean), untilDestroyed(this))
      .subscribe(() => this.doSave(instruction));
  }

  public reset(): void {
    this.buildForm(toRows(this.saved));
    this.jsonText = stringifyInstruction(this.saved);
    this.jsonError = null;
  }

  public retryLoad(): void {
    this.loadInstruction();
  }

  private doSave(instruction: ISpecialConvertInstruction): void {
    this.isSaving = true;

    this.api.save(this.targetOrganizationId, instruction)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: () => {
          this.isSaving = false;
          this.saved = instruction;
          this.snackbar.success(this.translate.instant('MENU.SPECIAL_INSTRUCTIONS.SAVED'));
        },
        error: (error: HttpErrorResponse) => {
          this.isSaving = false;
          // Форму не трогаем: правки не должны теряться из-за неудачного сохранения.
          this.snackbar.error(describeSpecialInstructionsError(error, this.translate));
        },
      });
  }

  private loadInstruction(): void {
    this.isLoading = true;
    this.loadError = null;
    this.isForbidden = false;

    this.api.get(this.targetOrganizationId)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: instruction => {
          this.isLoading = false;
          // Пустое тело — инструкций ещё нет, это нормальное состояние.
          this.saved = normalizeInstruction(instruction ?? EMPTY_INSTRUCTION);
          this.reset();
        },
        error: (error: HttpErrorResponse) => {
          this.isLoading = false;

          if (error.status === 403) {
            // Редактор не показываем вовсе: иначе пользователь заполнит форму и упрётся
            // в ту же 403 на сохранении.
            this.isForbidden = true;
            this.forbiddenDetails = extractServerMessage(error);
            return;
          }

          this.loadError = describeSpecialInstructionsError(error, this.translate);
        },
      });
  }

  /**
   * Шелл MenuMainComponent на этом роуте не отрабатывает, поэтому меню в сторе может
   * отсутствовать. Грузим сами и кладём в стор — страница меню потом переиспользует его.
   */
  private async ensureMenuLoaded(): Promise<void> {
    const menu = await firstValueFrom(this.store.select(MenuSelectors.selectMenu));

    if (menu?.products?.length) {
      return;
    }

    this.isMenuLoading = true;

    try {
      const response = await this.dishesService.getFullMenu(this.organizationId);

      if (response?.success && response.data) {
        // Именно setMenu: setFullMenuInfo заодно затёр бы menuSections и overridePositionsMap.
        this.store.dispatch(MenuActions.setMenu(response.data));
      } else {
        this.menuUnavailable = true;
      }
    } catch {
      this.menuUnavailable = true;
    } finally {
      this.isMenuLoading = false;
    }
  }

  private subscribeToDictionaries(): void {
    this.store.select(MenuSelectors.selectSpecialInstructionsDictionaries)
      .pipe(untilDestroyed(this))
      .subscribe(({modifiers, sizes}) => {
        this.modifierOptions = modifiers
          .map(product => ({id: product.externalId, label: product.name ?? '', hint: product.externalId}))
          .sort((first, second) => first.label.localeCompare(second.label));
        this.sizeOptions = sizes
          .map(size => ({id: size.externalId, label: size.name ?? '', hint: size.externalId}))
          .sort((first, second) => first.label.localeCompare(second.label));

        this.modifierById = new Map(this.modifierOptions.map(option => [option.id, option]));
        this.sizeById = new Map(this.sizeOptions.map(option => [option.id, option]));
      });
  }

  private subscribeToJsonInput(): void {
    this.jsonInput$
      .pipe(debounceTime(JSON_PARSE_DEBOUNCE_MS), untilDestroyed(this))
      .subscribe(text => this.applyJson(text));
  }

  private applyJson(text: string): void {
    const result = parseInstructionJson(text);

    if (result.error) {
      this.jsonError = this.translate.instant('MENU.SPECIAL_INSTRUCTIONS.JSON.INVALID', {message: result.error});
      return;
    }

    if (result.shapeError) {
      this.jsonError = this.translate.instant('MENU.SPECIAL_INSTRUCTIONS.JSON.SHAPE_INVALID');
      return;
    }

    this.jsonError = null;
    this.buildForm(toRows(result.value));
  }

  private readForm(): ISpecialInstructionsForm {
    return this.form.getRawValue() as ISpecialInstructionsForm;
  }

  private buildForm(value: ISpecialInstructionsForm): void {
    this.form = this.fb.group({
      modifiers: this.fb.array(value.modifiers.map(row => this.createModifierGroup(row))),
      ignoreModifiers: this.fb.control(value.ignoreModifiers),
    });
    this.expanded = value.modifiers.map(() => false);
  }

  private createModifierGroup(row: IModifierRow): UntypedFormGroup {
    return this.fb.group({
      // Неизвестный GUID валиден: проверяем только что поле заполнено.
      modifierId: this.fb.control(row.modifierId, Validators.required),
      isRemove: this.fb.control(row.isRemove),
      sizePairs: this.fb.array(row.sizePairs.map(pair => this.createSizePairGroup(pair))),
    });
  }

  private createSizePairGroup(pair: ISizePairRow): UntypedFormGroup {
    return this.fb.group({
      sourceSizeId: this.fb.control(pair.sourceSizeId, Validators.required),
      targetSizeId: this.fb.control(pair.targetSizeId, Validators.required),
    });
  }
}
